import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSnowflakeJobSettings, postSnowflakeJobSettings } from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { LoadingButton, Stack } from "@altimateai/lego";
import { BackButton } from "@/components/button";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Typography,
  toast,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupInput,
  Label,
  Separator,
  TagsInput,
  TagsInputInput,
  TagsInputItem,
  TagsInputList,
} from "@altimateai/lego";

const formSchema = z.object({
  prefix: z.string().nullable(),
  workflow_id: z.string().min(1, "Workload ID is required"),
  run_id: z.string().nullable().optional(),
  task_id: z.string().nullable().optional(),
  project_id: z.string().min(1, "Project ID is required"),
  app_id: z.string().min(1, "App ID is required"),
  custom_tags: z.array(z.string()).optional(),
});

type FormValues = z.infer<typeof formSchema>;

const SnowflakeJobSettings: React.FC = () => {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      prefix: "",
      workflow_id: "",
      run_id: "",
      task_id: "",
      project_id: "",
      app_id: "",
      custom_tags: [],
    },
  });

  const { data, isPending: isLoading } = useQuery({
    queryKey: ["snowflakeJobSettings"],
    queryFn: getSnowflakeJobSettings,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (data) {
      form.reset({
        ...data,
        custom_tags: data.custom_tags || [],
      });
    }
  }, [data, form]);

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: postSnowflakeJobSettings,
    onSuccess: () => {
      toast({ description: "Settings saved successfully" });
      queryClient.refetchQueries({ queryKey: ["snowflakeJobSettings"] });
    },
  });

  const onSubmit = (values: FormValues) => {
    mutate(values);
  };

  if (isLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} aria-label="Workload settings form">
        <Stack direction="column">
          <Stack className="mb-4">
            <BackButton defaultUrl="/query/snowflake-jobs" />
            <Typography variant="h4" weight="semibold">
              Workload Settings
            </Typography>
          </Stack>
          <Stack direction="column" className="gap-sm w-60">
            <Card className="al-p-4 al-border-0 al-shadow-none">
              <Typography variant="h4" weight="medium">
                Workload Prefix
              </Typography>
              <Typography variant="body" size="sm" className="al-text-dark-gray">
                Workload prefix is the path separated by &apos;.&apos; in query tag
              </Typography>
              <FormField
                control={form.control}
                name="prefix"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <InputGroup>
                        <InputGroupAddon align="inline-start">
                          <InputGroupText>Prefix</InputGroupText>
                        </InputGroupAddon>
                        <InputGroupInput {...field} value={field.value ?? ""} aria-label="Prefix" />
                      </InputGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Card>
            <Card className="al-p-4 al-border-0 al-shadow-none">
              <Typography variant="h4" weight="medium">
                Workload Definition
              </Typography>
              <Typography variant="body" size="sm" className="al-text-dark-gray">
                Workload definition represents the definition for the workload
              </Typography>
              <Stack direction="column">
                <FormField
                  control={form.control}
                  name="workflow_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <InputGroup>
                          <InputGroupAddon align="inline-start">
                            <InputGroupText>Workload ID</InputGroupText>
                          </InputGroupAddon>
                          <InputGroupInput
                            {...field}
                            value={field.value ?? ""}
                            aria-label="Workload ID"
                          />
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Stack className="al-w-full">
                  <div className="al-min-w-16" />
                  <FormField
                    control={form.control}
                    name="task_id"
                    render={({ field }) => (
                      <FormItem className="al-w-full">
                        <FormControl>
                          <InputGroup>
                            <InputGroupAddon align="inline-start">
                              <InputGroupText>Task ID</InputGroupText>
                            </InputGroupAddon>
                            <InputGroupInput
                              {...field}
                              value={field.value ?? ""}
                              aria-label="Task ID"
                            />
                          </InputGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </Stack>
                <FormField
                  control={form.control}
                  name="run_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <InputGroup>
                          <InputGroupAddon align="inline-start">
                            <InputGroupText>Run ID</InputGroupText>
                          </InputGroupAddon>
                          <InputGroupInput
                            {...field}
                            value={field.value ?? ""}
                            aria-label="Run ID"
                          />
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Stack>
            </Card>
            <Card className="al-p-4 al-border-0 al-shadow-none">
              <Typography variant="h4" weight="medium">
                Filters
              </Typography>
              <Typography variant="body" size="sm" className="al-text-dark-gray">
                Other information that we capture for better filtering support
              </Typography>
              <Stack direction="column">
                <FormField
                  control={form.control}
                  name="project_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <InputGroup>
                          <InputGroupAddon align="inline-start">
                            <InputGroupText>Project ID</InputGroupText>
                          </InputGroupAddon>
                          <InputGroupInput
                            {...field}
                            value={field.value ?? ""}
                            aria-label="Project ID"
                          />
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="app_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <InputGroup>
                          <InputGroupAddon align="inline-start">
                            <InputGroupText>App ID</InputGroupText>
                          </InputGroupAddon>
                          <InputGroupInput
                            {...field}
                            value={field.value ?? ""}
                            aria-label="App ID"
                          />
                        </InputGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Stack>
            </Card>
            <Separator className="al-my-4" />
            <Card className="al-p-4 al-border-0 al-shadow-none">
              <Typography variant="h4" weight="medium">
                Custom Tags (Optional)
              </Typography>
              <Typography variant="body" size="sm" className="al-text-dark-gray">
                Add custom tags for additional filtering. These tags will be visible as filters in
                the filter dropdown.
              </Typography>
              <Stack direction="column" className="al-gap-2">
                <Label>Custom Tags</Label>
                <FormField
                  control={form.control}
                  name="custom_tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <TagsInput
                          value={field.value || []}
                          onValueChange={field.onChange}
                          addOnPaste
                          delimiter=","
                        >
                          <TagsInputList>
                            {(field.value || []).map(tag => (
                              <TagsInputItem key={tag} value={tag}>
                                {tag}
                              </TagsInputItem>
                            ))}
                            <TagsInputInput placeholder="Type a tag and press Enter" />
                          </TagsInputList>
                        </TagsInput>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Stack>
            </Card>
            <Stack className="al-mt-4">
              <LoadingButton loading={isSaving || isLoading} type="submit">
                Save
              </LoadingButton>
              <Button variant="outline" asChild>
                <Link to="/query/snowflake-jobs">Cancel</Link>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </Form>
  );
};

export { SnowflakeJobSettings };
